
import { useState } from 'react';

export default function CTASection() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    service: '',
    message: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitMessage, setSubmitMessage] = useState('');

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.name || !formData.email || !formData.service) {
      setSubmitMessage('Please fill in all required fields');
      return;
    }

    setIsSubmitting(true);
    setSubmitMessage('');

    try {
      const formDataEncoded = new URLSearchParams();
      Object.entries(formData).forEach(([key, value]) => {
        formDataEncoded.append(key, value);
      });

      const response = await fetch('https://readdy.ai/api/form/d67h0n6a728k8ctu1sog', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formDataEncoded.toString(),
      });

      if (response.ok) {
        setSubmitMessage('Thank you! We will contact you shortly.');
        setFormData({ name: '', email: '', phone: '', service: '', message: '' });
      } else {
        setSubmitMessage('Something went wrong. Please try again.');
      }
    } catch (error) {
      setSubmitMessage('Failed to submit. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section className="py-24 bg-white">
      <div className="max-w-7xl mx-auto px-6">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          <div>
            <span className="inline-block px-4 py-2 bg-[#C9A961]/10 text-[#C9A961] text-sm font-medium rounded-full mb-4">
              Get In Touch
            </span>
            <h2 className="text-4xl lg:text-5xl font-bold text-[#1A1F2E] mb-6" style={{ fontFamily: 'Playfair Display, serif' }}>
              Plan Your London Visit Today
            </h2>
            <p className="text-lg text-gray-600 mb-8 leading-relaxed">
              Ready to explore London? Contact us for personalised recommendations on budget-friendly hotels, airport transfers, and business travel services tailored for Pakistani families.
            </p>

            <div className="space-y-6">
              <div className="flex items-center gap-4">
                <div className="w-14 h-14 bg-[#FFF8E7] rounded-xl flex items-center justify-center">
                  <i className="ri-phone-line text-xl text-[#C9A961]"></i>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Call Us</p>
                  <a href="tel:02034884455" className="font-bold text-[#1A1F2E] hover:text-[#C9A961] transition-colors cursor-pointer">0203 4884455</a>
                </div>
              </div>
              <div className="flex items-center gap-4">
                <div className="w-14 h-14 bg-[#FFF8E7] rounded-xl flex items-center justify-center">
                  <i className="ri-whatsapp-line text-xl text-[#C9A961]"></i>
                </div>
                <div>
                  <p className="text-sm text-gray-500">WhatsApp</p>
                  <a href="https://wa.me/447362505895" target="_blank" rel="noopener noreferrer" className="font-bold text-[#1A1F2E] hover:text-[#C9A961] transition-colors cursor-pointer">+44 7362 505895</a>
                </div>
              </div>
              <div className="flex items-center gap-4">
                <div className="w-14 h-14 bg-[#FFF8E7] rounded-xl flex items-center justify-center">
                  <i className="ri-mail-line text-xl text-[#C9A961]"></i>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Email</p>
                  <a href="mailto:contact@alwaztravels.com" className="font-bold text-[#1A1F2E] hover:text-[#C9A961] transition-colors cursor-pointer">contact@alwaztravels.com</a>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-[#FAFAFA] rounded-2xl p-8">
            <h3 className="text-2xl font-bold text-[#1A1F2E] mb-6">Send Us an Enquiry</h3>
            <form id="visit-london-inquiry" onSubmit={handleSubmit} data-readdy-form className="space-y-4">
              <div>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  placeholder="Your Name *"
                  className="w-full px-4 py-3 bg-white border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-[#C9A961] transition-colors"
                  disabled={isSubmitting}
                />
              </div>
              <div>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="Email Address *"
                  className="w-full px-4 py-3 bg-white border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-[#C9A961] transition-colors"
                  disabled={isSubmitting}
                />
              </div>
              <div>
                <input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  placeholder="Phone Number"
                  className="w-full px-4 py-3 bg-white border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-[#C9A961] transition-colors"
                  disabled={isSubmitting}
                />
              </div>
              <div>
                <select
                  name="service"
                  value={formData.service}
                  onChange={handleChange}
                  className="w-full px-4 py-3 bg-white border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-[#C9A961] transition-colors cursor-pointer"
                  disabled={isSubmitting}
                >
                  <option value="">Select Service *</option>
                  <option value="Budget Hotels">Budget Family Hotels</option>
                  <option value="Halal Accommodations">Halal-Friendly Accommodations</option>
                  <option value="Airport Transfer">Airport Transfer</option>
                  <option value="Business Trip">Business Trip Services</option>
                  <option value="Seminars">Seminars & Conferences</option>
                  <option value="City Tours">Guided City Tours</option>
                </select>
              </div>
              <div>
                <textarea
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  placeholder="Your Message (max 500 characters)"
                  maxLength={500}
                  rows={4}
                  className="w-full px-4 py-3 bg-white border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-[#C9A961] transition-colors resize-none"
                  disabled={isSubmitting}
                ></textarea>
              </div>
              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full py-4 bg-[#C9A961] text-white rounded-lg font-semibold hover:bg-[#B89851] transition-colors whitespace-nowrap cursor-pointer disabled:opacity-50"
              >
                {isSubmitting ? 'Sending...' : 'Submit Enquiry'}
              </button>
              {submitMessage && (
                <p className={`text-sm text-center ${submitMessage.includes('Thank you') ? 'text-green-600' : 'text-red-500'}`}>
                  {submitMessage}
                </p>
              )}
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}
